"use client";

/**
 * The product-card action control — the one thing in the card footer that
 * changes with buy state.
 *
 * Both cards carried the same four-branch cascade, in the same order, with the
 * same `aria-label`s and the same `Sold out` chip (that chip was already
 * byte-identical between them). Only the button's class string differed:
 * the desktop grid card shrinks to 36px on `sm+`, the mobile row stays 44px.
 *
 * Branch ORDER is the load-bearing part, and it must stay as written:
 *   1. simple + in stock            → quick-add
 *   2. variants + in stock          → open the drawer to pick an option, but
 *                                     keep a cart button visible rather than
 *                                     hiding the affordance
 *   3. not priceable                → enquire
 *   4. out of stock                 → a static `Sold out` chip, no control
 * Reordering 1 and 2 would let a variant product be added with no option
 * chosen; moving 4 above 3 would offer "Sold out" on a bespoke commission that
 * was never stocked.
 */

import { ShoppingCart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ProductEnquireOptions } from "@/components/enquire/ProductEnquireOptions";
import type { Product } from "@/stores/product-store";
import type { ProductCardModel } from "./product-card-model";

export function ProductCardActions({
  product,
  model,
  className,
  onOpen,
  onAddToCart,
}: {
  product: Product;
  model: ProductCardModel;
  /** Class string for the round action control — differs per card layout. */
  className: string;
  /** Open the in-page detail drawer. */
  onOpen: () => void;
  onAddToCart: (p: Product, e: React.MouseEvent) => void;
}) {
  const { priceable, hasVariants, canAddToCart, isOutOfStock } = model;
  return canAddToCart ? (
    <Button
      size="icon"
      className={className}
      onClick={(e) => {
        e.stopPropagation();
        onAddToCart(product, e);
      }}
      aria-label="Add to cart"
    >
      <ShoppingCart className="h-4 w-4" />
    </Button>
  ) : priceable && hasVariants && product.inStock >= 1 ? (
    // Variant products can't be added in one tap — keep the cart button
    // visible but open the detail drawer so the customer picks an option.
    <Button
      size="icon"
      className={className}
      onClick={(e) => {
        e.stopPropagation();
        onOpen();
      }}
      aria-label="Choose options"
    >
      <ShoppingCart className="h-4 w-4" />
    </Button>
  ) : !priceable ? (
    <ProductEnquireOptions
      productTitle={product.title}
      productId={product.id}
      productSlug={product.slug}
      variant="icon"
      className={className}
    />
  ) : isOutOfStock ? (
    <span className="rounded-full bg-muted px-3 py-1.5 text-xs font-medium text-muted-foreground">
      Sold out
    </span>
  ) : null;
}
